<template>
  <div
    v-if="!isLoading && !bottles.length"
    class="bg-gray-50 shadow-none h-80 mt-4 flex flex-col items-center text-gray-500 content-center justify-center border-2 border-dashed sm:rounded-md"
  >
    <div class="text-xl font-medium text-brand-800">{{ emptyStateHeader }}</div>
    <div>{{ emptyStateSubheader }}</div>
  </div>
  <TableContainer :is-empty="isEmpty" :is-loading="isLoading" :has-error="hasError" :flush="stacked">
    <template v-if="bottles.length" #table>
      <table class="min-w-full shadow sm:rounded-lg bg-white" :class="stacked && 'bottles-list--stacked'">
        <thead class="border-b border-cool-gray-200 bg-cool-gray-50">
          <tr class="text-left">
            <th scope="col" class="pl-4 py-3 text-gray-700 text-xs font-medium tracking-wider leading-4 uppercase">
              Order
            </th>
            <th scope="col" class="px-2 py-3 text-gray-700 text-xs font-medium tracking-wider leading-4 uppercase">
              Details
            </th>
            <th scope="col" class="px-2 py-3 text-gray-700 text-xs font-medium tracking-wider leading-4 uppercase">
              Fulfillment
            </th>
            <th scope="col" class="px-2 py-3 text-gray-700 text-xs font-medium tracking-wider leading-4 uppercase">
              Cart
            </th>
            <th
              v-if="false"
              scope="col"
              class="pr-6 py-3 text-gray-700 text-xs font-medium tracking-wider leading-4 uppercase"
            ></th>
          </tr>
        </thead>
        <tbody class="divide-cool-gray-200 divide-y">
          <tr
            v-for="item in bottles.slice(local.page * pageSize, local.page * pageSize + pageSize)"
            :key="item.id"
            class="leading-none"
            :class="`${item.expanded ? '' : 'h-16'}`"
          >
            <!-- COLUMN: Order id, payment, cancellation, refund, skip status -->
            <td class="whitespace-no-wrap pl-4 py-2" data-label="Order">
              <div class="text-gray-700">
                <!-- Main order ID -->
                <div class="flex items-center -ml-1 mb-1">
                  <!-- ORDER ID -->
                  <router-link
                    v-if="canReadOrders"
                    :to="`/order/${item.id}`"
                    class="mr-1 cursor-pointer whitespace-nowrap px-1 capitalize inline-flex text-md leading-5 font-semibold rounded-sm hover:underline"
                    :class="
                      getBottleCancellationStatus(item) !== 'cancelled' &&
                      paymentStatus(item.attributes.status, item.relationships?.bottle_payment?.data?.id).classTheme ==
                        'success'
                        ? 'bg-green-100 text-green-900'
                        : {
                            'bg-red-100 text-red-800': getBottleCancellationStatus(item) === 'cancelled',
                            'bg-gray-100 text-gray-800': getBottleCancellationStatus(item) !== 'cancelled'
                          }
                    "
                  >
                    <span class="tracking-tight">{{ item.attributes?.display_id }}</span>
                    <!-- Add "Edit" language here that opens order view -->
                    <!-- Add an "Open link" icon here to open order in external window / open receipt -->
                  </router-link>
                  <div
                    v-else
                    class="mr-1 whitespace-nowrap px-1 capitalize inline-flex text-md leading-5 font-semibold rounded-sm"
                    :class="
                      getBottleCancellationStatus(item) !== 'cancelled' &&
                      paymentStatus(item.attributes.status, item.relationships?.bottle_payment?.data?.id).classTheme ==
                        'success'
                        ? 'bg-green-100 text-green-900'
                        : {
                            'bg-red-100 text-red-800': getBottleCancellationStatus(item) === 'cancelled',
                            'bg-gray-100 text-gray-800': getBottleCancellationStatus(item) !== 'cancelled'
                          }
                    "
                  >
                    <span class="tracking-tight">{{ item.attributes?.display_id }}</span>
                  </div>
                </div>

                <!-- Cancellation status -->
                <div v-if="getBottleCancellationStatus(item) === 'cancelled'" class="flex items-center -ml-1 mb-1">
                  <!-- CANCELLATION STATUS -->
                  <div
                    class="whitespace-nowrap px-1 capitalize inline-flex text-sm leading-5 font-semibold rounded-md mr-1 bg-red-100 text-red-800"
                  >
                    <span class="manrope text-xxs font-medium uppercase tracking-wide">Cancelled</span>
                  </div>
                </div>

                <!-- Order payment status and other info and info -->
                <div
                  v-if="
                    item?.attributes?.refund_status !== 'fully_refunded' &&
                    item?.attributes?.refund_status !== 'partially_refunded'
                  "
                  class="flex items-center -ml-1 mb-1"
                >
                  <!-- PAYMENT STATUS -->
                  <div
                    v-if="
                      paymentStatus(item.attributes.status, item.relationships?.bottle_payment?.data?.id).classTheme !=
                      'default'
                    "
                    class="whitespace-nowrap px-1 capitalize inline-flex text-sm leading-5 font-semibold rounded-md mr-1"
                    :class="{
                      'bg-gray-100 text-gray-700':
                        paymentStatus(item.attributes.status, item.relationships?.bottle_payment?.data?.id)
                          .classTheme == 'default',
                      'bg-green-100 text-green-800':
                        paymentStatus(item.attributes.status, item.relationships?.bottle_payment?.data?.id)
                          .classTheme == 'success',
                      'bg-red-100 text-red-800':
                        paymentStatus(item.attributes.status, item.relationships?.bottle_payment?.data?.id)
                          .classTheme == 'error',
                      'bg-yellow-100 text-yellow-800':
                        paymentStatus(item.attributes.status, item.relationships?.bottle_payment?.data?.id)
                          .classTheme == 'warning'
                    }"
                  >
                    <span
                      v-if="
                        getBottlePaymentObject(item.relationships.bottle_payment?.data?.id)?.attributes?.reason ===
                          'stripe_payment' &&
                        getCheckoutObject(
                          getBottlePaymentObject(item.relationships.bottle_payment?.data?.id)?.relationships?.checkout
                            ?.data?.id
                        )?.attributes?.bottle_total?.format
                      "
                      class="manrope text-xxs font-medium uppercase tracking-wide"
                    >
                      {{ paymentStatus(item.attributes.status, item.relationships?.bottle_payment?.data?.id)?.text }}
                      {{
                        getCheckoutObject(
                          getBottlePaymentObject(item.relationships.bottle_payment?.data?.id)?.relationships?.checkout
                            ?.data?.id
                        )?.attributes?.bottle_total?.format
                      }}
                    </span>
                    <span v-else class="manrope text-xxs font-medium uppercase tracking-wide">
                      {{ paymentStatus(item.attributes.status, item.relationships?.bottle_payment?.data?.id)?.text }}
                    </span>
                  </div>
                </div>

                <!-- Order charged and account credit used. An open invoice has
                     nothing charged yet; the status above already says Invoiced (BOT-1680). -->
                <div
                  v-if="
                    getBottlePaymentObject(item.relationships.bottle_payment?.data?.id)?.attributes &&
                    !awaitingInvoicePayment(
                      item.attributes,
                      getBottlePaymentObject(item.relationships.bottle_payment?.data?.id)
                    )
                  "
                  class="flex items-center -ml-1 mb-1"
                >
                  <!-- PAYMENT STATUS -->
                  <div
                    class="whitespace-nowrap px-1 capitalize text-sm leading-5 font-semibold rounded-md mr-1 bg-gray-100 text-gray-700"
                  >
                    <!-- BOT-1764: marked paid outside Stripe means no card money moved -->
                    <span
                      v-if="collectedOutsideStripe(getBottlePaymentObject(item.relationships.bottle_payment?.data?.id))"
                      class="manrope text-xxs font-medium uppercase tracking-wide"
                    >
                      Marked as paid
                    </span>
                    <span
                      v-else-if="
                        getBottlePaymentObject(item.relationships.bottle_payment?.data?.id)?.attributes?.charged_amount
                          ?.format
                      "
                      class="manrope text-xxs font-medium uppercase tracking-wide"
                    >
                      {{
                        getBottlePaymentObject(item.relationships.bottle_payment?.data?.id)?.attributes?.charged_amount
                          ?.format
                      }}&nbsp;Charged
                    </span>
                    <span
                      v-if="
                        getBottlePaymentObject(item.relationships.bottle_payment?.data?.id)?.attributes
                          ?.account_credit_amount?.amount > 0
                      "
                      class="manrope text-xxs font-medium uppercase tracking-wide"
                    >
                      {{
                        getBottlePaymentObject(item.relationships.bottle_payment?.data?.id)?.attributes?.charged_amount
                          ?.format
                      }}&nbsp;Credit Used
                    </span>
                  </div>
                </div>

                <!-- Refund status -->
                <div
                  v-if="
                    item?.attributes?.refund_status == 'fully_refunded' ||
                    item?.attributes?.refund_status == 'partially_refunded'
                  "
                  class="flex items-center -ml-1 mb-1"
                >
                  <!-- REFUND STATUS -->
                  <div
                    class="whitespace-nowrap px-1 capitalize inline-flex text-sm leading-5 font-semibold rounded-md mr-1 bg-yellow-100 text-yellow-800"
                  >
                    <span
                      v-if="item.attributes.refund_status === 'partially_refunded'"
                      class="manrope text-xxs font-medium uppercase tracking-wide"
                    >
                      Refunded {{ refundAmount(item) }}
                    </span>
                    <span
                      v-if="item.attributes.refund_status === 'fully_refunded'"
                      class="manrope text-xxs font-medium uppercase tracking-wide"
                    >
                      Fully Refunded
                    </span>
                  </div>
                </div>

                <!-- Skipped info, if skipped -->
                <div v-if="item.attributes.status === 'skipped'" class="flex items-center -ml-1">
                  <!-- SKIPPED STATUS -->
                  <div
                    class="whitespace-nowrap px-1 capitalize inline-flex text-sm leading-5 font-semibold rounded-md mr-1 bg-gray-100 text-gray-800"
                  >
                    <span class="manrope text-xxs font-medium uppercase tracking-wide">Skipped</span>
                  </div>
                </div>
              </div>
            </td>

            <!-- COLUMN: Order details including buyer's name, production day and method, store, drop if applicable, and any errors that need to be addressed -->
            <td class="whitespace-no-wrap pl-4 py-2 pb-3" data-label="Details">
              <!-- CUSTOMER NAME -->
              <router-link
                v-if="
                  canReadCustomerProfile &&
                  item.relationships?.conversation_user_pair?.data &&
                  getCustomerObject(item.relationships.conversation_user_pair?.data?.id)
                "
                class="mt-1 leading-5 truncate max-w-xxs block text-sm cursor-pointer text-gray-800 hover:text-gray-900 hover:underline font-semibold tracking-tight"
                :to="'/profile/' + getCustomerObject(item.relationships.conversation_user_pair?.data?.id)?.id"
              >
                {{ getCustomerObject(item.relationships.conversation_user_pair?.data?.id)?.attributes?.display_name }}
              </router-link>
              <div
                v-else-if="
                  item.relationships?.conversation_user_pair?.data &&
                  getCustomerObject(item.relationships.conversation_user_pair?.data?.id)
                "
                class="mt-1 leading-5 truncate max-w-xxs block text-sm text-gray-800 font-semibold tracking-tight"
              >
                {{ getCustomerObject(item.relationships.conversation_user_pair?.data?.id)?.attributes?.display_name }}
              </div>

              <!-- paid date -->
              <div
                v-if="
                  item.relationships?.bottle_payment?.data && getPaidDate(item.relationships?.bottle_payment?.data?.id)
                "
                class="mt-1"
              >
                <span class="text-gray-500 text-xs leading-5 whitespace-nowrap tracking-tight font-semibold">
                  {{ getPaidDate(item.relationships.bottle_payment?.data?.id) }}
                  <span
                    v-if="
                      item.relationships?.bottle_payment?.data &&
                      getPaidTime(item.relationships?.bottle_payment?.data?.id)
                    "
                  >
                    at {{ getPaidTime(item.relationships.bottle_payment?.data?.id) }}
                  </span>
                </span>
                <div class="text-xxs font-manrope uppercase tracking-wider text-gray-500">Paid time</div>
              </div>

              <!-- membership tier and list, if applicable -->
              <div
                v-if="
                  item.relationships?.membership_tier?.data &&
                  getMembershipTier(item.relationships?.membership_tier?.data?.id)
                "
                class="mt-1"
              >
                <router-link
                  v-if="canReadMembershipTiers"
                  :to="
                    '/store/membership-tiers/' + getMembershipTier(item.relationships?.membership_tier?.data?.id)?.id
                  "
                  class="text-gray-500 text-xs leading-5 whitespace-nowrap font-semibold tracking-tight hover:underline"
                >
                  {{ getMembershipTier(item.relationships?.membership_tier?.data?.id)?.attributes?.tier_name }}
                </router-link>
                <div v-else class="text-gray-500 text-xs leading-5 whitespace-nowrap font-semibold tracking-tight">
                  {{ getMembershipTier(item.relationships?.membership_tier?.data?.id)?.attributes?.tier_name }}
                </div>
                <div class="text-xxs font-manrope uppercase tracking-wider text-gray-500">Membership</div>
              </div>

              <!-- name of drop, if applicable -->
              <div
                v-if="
                  item.attributes?.bottle_cycle_details &&
                  getBottleCycle(item.attributes?.bottle_cycle_details?.bottle_cycle_id)
                "
                class="mt-1"
              >
                <router-link
                  v-if="canReadDrops"
                  :to="'/store/drops/' + getBottleCycle(item.attributes?.bottle_cycle_details?.bottle_cycle_id)?.id"
                  class="text-gray-500 text-xs leading-5 whitespace-nowrap font-semibold tracking-tight hover:underline"
                >
                  {{ getBottleCycle(item.attributes?.bottle_cycle_details?.bottle_cycle_id)?.attributes?.description }}
                </router-link>
                <div v-else class="text-gray-500 text-xs leading-5 whitespace-nowrap font-semibold tracking-tight">
                  {{ getBottleCycle(item.attributes?.bottle_cycle_details?.bottle_cycle_id)?.attributes?.description }}
                </div>
                <div class="text-xxs font-manrope uppercase tracking-wider text-gray-500">Drop</div>
              </div>
            </td>

            <!-- COLUMN: Fulfillment info -->
            <td class="whitespace-no-wrap px-2 py-2" data-label="Fulfillment">
              <div
                v-if="
                  item.relationships?.fulfillment_slot?.data?.id &&
                  getFulfilledDate(item.relationships?.fulfillment_slot?.data?.id)
                "
                class="mt-1"
              >
                <router-link
                  v-if="canReadLists"
                  :to="'/lists/' + getFulfillmentSlot(item)?.relationships?.audience_for_date?.data?.id"
                  class="text-gray-500 text-xs leading-5 whitespace-nowrap font-semibold tracking-tight hover:underline"
                >
                  {{ getFulfilledDate(item.relationships.fulfillment_slot?.data?.id) }}
                </router-link>
                <div v-else class="text-gray-500 text-xs leading-5 whitespace-nowrap font-semibold tracking-tight">
                  {{ getFulfilledDate(item.relationships.fulfillment_slot?.data?.id) }}
                </div>
                <div class="text-xxs font-manrope uppercase tracking-wider text-gray-500">Production Day</div>
              </div>

              <div
                v-if="
                  item.relationships?.fulfillment_slot?.data?.id &&
                  getFulfillmentMethod(item.relationships?.fulfillment_slot?.data?.id)
                "
                class="mt-1"
              >
                <router-link
                  v-if="
                    canReadFulfillmentMethods && getFulfillmentMethod(item.relationships.fulfillment_slot.data.id)?.id
                  "
                  :to="
                    '/store/fulfillment-methods/' +
                    getFulfillmentMethod(item.relationships.fulfillment_slot.data.id)?.id
                  "
                  class="text-gray-500 text-xs leading-5 whitespace-nowrap font-semibold tracking-tight hover:underline"
                >
                  {{ getFulfillmentMethod(item.relationships.fulfillment_slot?.data?.id)?.attributes?.name }}
                </router-link>
                <div v-else class="text-gray-500 text-xs leading-5 whitespace-nowrap font-semibold tracking-tight">
                  {{ getFulfillmentMethod(item.relationships.fulfillment_slot?.data?.id)?.attributes?.name }}
                </div>
                <div class="text-xxs font-manrope uppercase tracking-wider text-gray-500">Method</div>
              </div>

              <button
                v-if="false"
                v-show="item.attributes.validations?.all_errors?.length"
                v-tippy="{
                  content: `${item.attributes.validations?.all_errors?.[0]}`,
                  placement: 'right'
                }"
                class="text-gray-500 hover:text-blue-500 ml-2"
              >
                <InformationCircleIcon class="w-4 h-4" />
              </button>
            </td>

            <!-- COLUMN: Cart and underlying cart items -->
            <td class="max-w-xxs px-2 py-2" data-label="Cart">
              <div data-test="cart-summary" class="bg-gray-100 rounded-lg pb-1 w-full overflow-hidden">
                <!-- Store info -->
                <router-link
                  v-if="
                    item.relationships?.store?.data?.id &&
                    getStoreObject(item.relationships.store?.data?.id) &&
                    canReadStores
                  "
                  class="w-full truncate block text-gray-500 text-xs leading-5 whitespace-nowrap font-semibold tracking-tight hover:underline whitespace-no-wrap bg-gray-200 px-2 py-1"
                  :to="'/store/stores/' + getStoreObject(item.relationships.store?.data?.id)?.id"
                >
                  {{ getStoreObject(item.relationships.store.data?.id)?.attributes?.store_name }}
                </router-link>
                <div
                  v-else-if="item.relationships?.store?.data?.id && getStoreObject(item.relationships.store?.data?.id)"
                  class="w-full truncate block text-gray-500 text-xs leading-5 whitespace-nowrap font-semibold tracking-tight whitespace-no-wrap bg-gray-200 px-2 py-1"
                >
                  {{ getStoreObject(item.relationships.store.data?.id)?.attributes?.store_name }}
                </div>

                <!-- Items in the cart -->
                <div
                  class="flex flex-col px-2 py-1 text-gray-500 text-xs leading-5 whitespace-nowrap font-semibold tracking-tight"
                >
                  <div
                    v-if="!cartItemsForBottle(item, true).length"
                    data-test="empty-cart"
                    class="flex items-center gap-1.5 font-medium text-gray-400"
                  >
                    <ShoppingCartIcon class="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    Empty cart
                  </div>
                  <div v-for="cartItem in cartItemsForBottle(item, item.expanded)" :key="cartItem?.id">
                    <div class="leading-5 truncate">
                      <span class="font-semibold text-right">{{ cartItem.attributes.quantity }}</span>
                      <span class="font-medium ml-1">
                        {{ mapRelationship(cartItem.relationships.product?.data)?.attributes?.product_name }}
                      </span>
                    </div>
                  </div>
                  <button
                    v-if="cartItemsForBottle(item, item.expanded).length > 2"
                    class="w-max mt-1 justify-center text-gray-500 text-xs hover:underline focus:outline-none tracking-tighter font-bold"
                    @click="expandCollapseCartItemsForRow(item)"
                  >
                    {{ item.expanded ? 'Show fewer' : 'Show more items' }}
                  </button>
                </div>
              </div>
            </td>

            <!-- COLUMN: Actions to take -->
            <td v-if="showActions && item.attributes.actions" class="text-right pr-4 py-2" data-label="">
              <DropdownButton
                :menu-items="getItemActionsMenu(item.attributes)"
                :item-id="parseFloat(item.id)"
                @on-actions-menu-click="handleActionsMenuClick"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </template>
    <template v-if="bottles.length" #tablePagination>
      <TablePagination
        v-if="pageSize < bottles.length"
        :items="bottles"
        :page-size="pageSize"
        :page="local.page"
        :initial-page="initialPage"
        :total-records="totalRecords || bottles.length"
        @on-page-change="handlePageChange"
      />
    </template>
  </TableContainer>
  <RefundModal
    :is-open="local.modal.type === 'refundModal'"
    :max-refund-cents="local.modal.content.maxRefundCents || 0"
    :is-loading="local.status.isLoading.refundModal"
    :error-message="local.modal.content.onSubmitMessage || ''"
    :error-log="local.modal.content.onSubmitLog || ''"
    @close="resetModalContent()"
    @submit="saveRefundModal"
  />
</template>
<script>
import { ClipboardDocumentListIcon, InformationCircleIcon, ShoppingCartIcon } from '@heroicons/vue/24/outline'
import { computed, defineComponent, ref } from 'vue'

import RefundModal from '@/components/neue/RefundModal.vue'
import { useEntitlements } from '@/composables/useEntitlements'
import { useToast } from '@/composables/useToast'
import { canOpenTierView } from '@/config/membershipTierView'
import { serverDayToDateObject } from '@/helpers/dates'
import { awaitingInvoicePayment, collectedOutsideStripe } from '@/helpers/orderPayment'
import { maxRefundCentsFor } from '@/helpers/refund'
import privilegeService from '@/services/privilege.service'
import api from '@/store/api'
import { mapRelationship, mapRelationships } from '@/store/mappers'

export default defineComponent({
  components: {
    ClipboardDocumentListIcon,
    InformationCircleIcon,
    RefundModal,
    ShoppingCartIcon
  },
  props: {
    bottles: {
      type: Object,
      required: true
    },
    showActions: {
      type: Boolean,
      required: false,
      default: false
    },
    isLoading: {
      type: Boolean,
      required: true,
      default: false
    },
    isEmpty: {
      type: Boolean,
      required: true,
      default: false
    },
    hasError: {
      type: Boolean,
      required: true,
      default: false
    },
    emitPagination: {
      type: Boolean,
      required: false,
      default: false
    },
    pageSize: {
      type: Number,
      required: false,
      default: 25
    },
    onPageChange: {
      type: Function,
      required: false,
      default: null
    },
    totalRecords: {
      type: Number,
      required: false,
      default: null
    },
    initialPage: {
      type: Number,
      required: false,
      default: 0
    },
    emptyStateHeader: {
      type: String,
      required: false,
      default: 'No results found'
    },
    emptyStateSubheader: {
      type: String,
      required: false,
      default: "Try adjusting your search to find what you're looking for"
    },
    tab: {
      type: String,
      required: true,
      default: 'contacts'
    },
    stacked: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['on-page-change', 'on-action-executed'],
  setup(props, { emit }) {
    const local = ref({
      bottles: props.bottles.map(bottle => {
        return {
          ...bottle,
          expanded: false
        }
      }),
      page: 0,
      modal: {
        mode: '',
        type: '',
        content: {}
      },
      status: {
        isLoading: {
          refundModal: false
        }
      }
    })

    const getStoreObject = id => {
      return mapRelationship({ type: 'store', id: id })
    }

    const expandCollapseCartItemsForRow = bottle => {
      bottle.expanded = !bottle.expanded
    }

    const getCheckoutObject = id => {
      return mapRelationship({ type: 'checkout', id: id })
    }

    const getBottlePaymentObject = id => {
      return mapRelationship({ type: 'bottle_payment', id: id })
    }

    const getFulfilledDate = id => {
      const fulfillment = mapRelationship({ type: 'fulfillment_slot', id: id })
      if (!fulfillment) return null
      return new Intl.DateTimeFormat('en-US', {
        dateStyle: 'medium'
      }).format(serverDayToDateObject(fulfillment.attributes.fulfillment_date))
    }

    const cartItemsForBottle = (bottle, expanded) => {
      const cart = mapRelationship(bottle.relationships?.cart?.data)
      const items = mapRelationships(cart?.relationships?.cart_items?.data) ?? []
      return expanded ? items : items.slice(0, 3)
    }

    const getFulfillmentSlot = item => {
      return mapRelationship(item?.relationships?.fulfillment_slot?.data)
    }

    const getFulfillmentMethod = id => {
      const fulfillmentSlot = mapRelationship({ type: 'fulfillment_slot', id: id })
      const fulfillmentMethod = mapRelationship(fulfillmentSlot?.relationships?.fulfillment_method?.data)
      if (!fulfillmentMethod) return null
      return fulfillmentMethod
    }

    const getCustomerObject = id => {
      return mapRelationship({ type: 'conversation_user_pair', id: id })
    }

    const getPaidDate = id => {
      const payment = mapRelationship({ type: 'bottle_payment', id: id })
      if (!payment) return null
      return new Intl.DateTimeFormat('en-US', {
        dateStyle: 'medium'
      }).format(Date.parse(payment.attributes.created_at))
    }

    const getPaidTime = id => {
      const payment = mapRelationship({ type: 'bottle_payment', id: id })
      if (!payment) return null
      return new Intl.DateTimeFormat([], {
        hour: 'numeric',
        minute: '2-digit'
      }).format(new Date(payment.attributes.created_at))
    }

    const getMembershipTier = id => {
      return mapRelationship({ type: 'membership_tier', id: id })
    }

    const getDistributionList = id => {
      return mapRelationship({ type: 'distribution_list', id: id })
    }

    const getBottleCycle = id => {
      return mapRelationship({ type: 'bottle_cycle', id: id })
    }

    const getBottleCancellationStatus = bottle => {
      return bottle?.attributes?.cancellation_status
    }

    const canReadOrders = computed(() => privilegeService.canAccessResourceAction('orders', null, 'read'))
    const canUpdateOrders = computed(() => privilegeService.canAccessResourceAction('orders', null, 'update'))
    const canReadStores = computed(() => privilegeService.canAccessResourceAction('sell', null, 'read'))
    const canReadLists = computed(() => privilegeService.canAccessResourceAction('customers', null, 'read'))
    const canReadFulfillmentMethods = computed(() => privilegeService.canAccessResourceAction('sell', null, 'read'))
    const canReadCustomerProfile = computed(() =>
      privilegeService.canAccessResourceAction('customer_profile', null, 'read')
    )
    const { user } = useEntitlements()
    const canReadMembershipTiers = computed(() => canOpenTierView(user.value))
    const canReadDrops = computed(() => privilegeService.canAccessResourceAction('sell', null, 'read'))

    const getItemActionsMenu = attributes => {
      let actionsMenu = {
        label: 'Actions',
        items: []
      }
      if (attributes.link && canReadOrders.value)
        actionsMenu.items.push({
          name: 'View order',
          handler: 'handleViewOrder'
        })
      if (attributes.actions.refund && canUpdateOrders.value)
        actionsMenu.items.push({
          name: 'Refund order',
          handler: 'handleRefund'
        })
      if (attributes.actions.mark_as_paid && canUpdateOrders.value)
        actionsMenu.items.push({
          name: 'Mark as paid',
          handler: 'handleMarkAsPaid'
        })
      if (attributes.actions.mark_as_unpaid && canUpdateOrders.value)
        actionsMenu.items.push({
          name: 'Mark as unpaid',
          handler: 'handleMarkAsUnpaid'
        })
      if (attributes.actions.cancel_order && canUpdateOrders.value)
        actionsMenu.items.push({
          name: 'Cancel order',
          handler: 'handlerCancelOrder'
        })
      if (attributes.actions.un_cancel_order && canUpdateOrders.value)
        actionsMenu.items.push({
          name: 'Reactivate order',
          handler: 'handlerReactivateOrder'
        })
      return actionsMenu
    }

    const { showToast } = useToast()

    // BOT-1764: unmarking an invoice order reissues the invoice and leaves the
    // order invoiced, so take the status from the reply and load its payment.
    const markPayment = (id, action, status) =>
      api
        .call('POST', `bottles/${id}/${action}?included=bottle_invoice,bottle_payment`)
        .then(response => {
          const bottle = props.bottles.find(x => parseFloat(x.id) === id)
          if (bottle) bottle.attributes.status = response?.data?.data?.attributes?.status ?? status
        })
        .catch(() => showToast("Couldn't update the payment. Please try again.", { type: 'error' }))

    const handleMarkAsPaid = id => markPayment(id, 'pay', 'paid')

    const handleMarkAsUnpaid = id => markPayment(id, 'unpay', 'pending')

    // POST: cancel order
    const handlerCancelOrder = id => {
      api
        .call('PUT', `bottles/${id}`, {
          bottle: {
            cancellation_status: 'cancelled'
          }
        })
        .then(() => {
          emit('on-action-executed')
        })
    }

    // POST: reactivate order
    const handlerReactivateOrder = id => {
      api
        .call('PUT', `bottles/${id}`, {
          bottle: {
            cancellation_status: 'active'
          }
        })
        .then(() => {
          emit('on-action-executed')
        })
    }

    // POST: apply refund
    const saveRefundModal = refundAmountCents => {
      const id = local.value.modal.content.bottleId
      local.value.status.isLoading.refundModal = true

      api
        .call('POST', `bottles/${id}/refunds`, {
          refund: {
            amount: refundAmountCents
          }
        })
        .then(() => {
          local.value.status.isLoading.refundModal = false
          resetModalContent()
        })
        .catch(error => {
          local.value.status.isLoading.refundModal = false
          local.value.modal.content.onSubmitMessage = 'Something went wrong!'
          local.value.modal.content.onSubmitLog = error
        })
    }

    const handleRefund = id => {
      const item = props.bottles.find(x => parseFloat(x.id) === parseFloat(id))
      const payment = getBottlePaymentObject(item?.relationships?.bottle_payment?.data?.id)
      const checkout = getCheckoutObject(item?.relationships?.checkout?.data?.id)
      const maxRefundCents = maxRefundCentsFor(payment, checkout)
      local.value.modal.type = 'refundModal'
      local.value.modal.mode = 'add'
      local.value.modal.content = {
        bottleId: id,
        maxRefundCents
      }
    }

    const refundAmount = item => {
      if (item.attributes.refund_status !== 'not_refunded') {
        return ` - ${getCheckoutObject(item.relationships.checkout?.data?.id)?.attributes?.refunded_amount?.format}`
      }
    }

    const handleViewOrder = id => {
      const bottleLink = props.bottles.find(x => parseFloat(x.id) === id).attributes.link ?? null
      if (bottleLink) window.open(bottleLink, '_blank')
      return
    }

    const handleActionsMenuClick = (action, id) => {
      switch (action) {
        case 'handleMarkAsPaid':
          return handleMarkAsPaid(id)
        case 'handleMarkAsUnpaid':
          return handleMarkAsUnpaid(id)
        case 'handleRefund':
          return handleRefund(id)
        case 'handleViewOrder':
          return handleViewOrder(id)
        case 'handlerCancelOrder':
          return handlerCancelOrder(id)
        case 'handlerReactivateOrder':
          return handlerReactivateOrder(id)
      }
    }

    const handlePageChange = p => {
      if (props.emitPagination) emit('on-page-change', p)
      else local.value.page = p
    }

    const resetModalContent = () => {
      local.value.modal.type = ''
      local.value.modal.mode = ''
      local.value.modal.content = {}
    }

    const paymentStatus = (paymentStatus, bottlePaymentId) => {
      // return paymentStatus, bottlePaymentId
      if (bottlePaymentId) {
        const bottlePayment = getBottlePaymentObject(bottlePaymentId)
        const orderMarkedAsPaid =
          bottlePayment?.attributes?.reason == 'mark_as_paid' || bottlePayment?.attributes?.reason == 'less_than_50c'
        if (orderMarkedAsPaid && paymentStatus == 'paid')
          return {
            text: 'Marked Paid',
            classTheme: 'success'
          }
      }
      if (paymentStatus == 'paid')
        return {
          text: 'Paid',
          classTheme: 'success'
        }
      else if (paymentStatus === 'invoiced')
        // BOT-1680: placed on an invoice that is still open.
        return {
          text: 'Invoiced',
          classTheme: 'warning'
        }
      else if (paymentStatus == 'expired')
        return {
          text: 'Expired',
          classTheme: 'error'
        }
      else if (paymentStatus == 'benign' || paymentStatus == 'skipped')
        return {
          text: paymentStatus.replace('-', ' '),
          classTheme: 'default'
        }
      else
        return {
          text: paymentStatus.replace('-', ' '),
          classTheme: 'warning'
        }
    }

    return {
      local,
      getStoreObject,
      getCheckoutObject,
      getBottlePaymentObject,
      getFulfilledDate,
      getFulfillmentMethod,
      getFulfillmentSlot,
      getCustomerObject,
      getPaidDate,
      refundAmount,
      getPaidTime,
      getItemActionsMenu,
      getMembershipTier,
      getDistributionList,
      getBottleCycle,
      handleActionsMenuClick,
      saveRefundModal,
      resetModalContent,
      handlePageChange,
      paymentStatus,
      awaitingInvoicePayment,
      collectedOutsideStripe,
      handlerCancelOrder,
      handlerReactivateOrder,
      getBottleCancellationStatus,
      cartItemsForBottle,
      expandCollapseCartItemsForRow,
      mapRelationship,
      serverDayToDateObject,
      canReadOrders,
      canReadStores,
      canReadLists,
      canReadFulfillmentMethods,
      canReadCustomerProfile,
      canReadMembershipTiers,
      canReadDrops
    }
  }
})
</script>
<style scoped>
@media (max-width: 820px) {
  .bottles-list--stacked,
  .bottles-list--stacked tbody,
  .bottles-list--stacked tr,
  .bottles-list--stacked td {
    display: block;
    width: 100%;
  }
  .bottles-list--stacked thead {
    display: none;
  }
  .bottles-list--stacked tbody tr {
    height: auto;
    border-top: 1px solid #e5e7eb;
    padding: 12px 10px 14px;
  }
  .bottles-list--stacked tbody tr:first-child {
    border-top: 0;
  }
  .bottles-list--stacked td {
    display: flex;
    max-width: none;
    align-items: baseline;
    gap: 12px;
    border: 0;
    padding: 4px 0;
    text-align: left;
  }
  .bottles-list--stacked td::before {
    content: attr(data-label);
    width: 88px;
    flex: none;
    padding-top: 2px;
    font-size: 10.5px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #6b7280;
  }
  .bottles-list--stacked td[data-label='']::before {
    display: none;
  }
  .bottles-list--stacked td[data-label=''] {
    padding-left: 100px;
  }
}
@media (max-width: 560px) {
  .bottles-list--stacked td {
    flex-direction: column;
    gap: 0;
  }
  .bottles-list--stacked td::before {
    width: auto;
  }
  .bottles-list--stacked td[data-label=''] {
    padding-left: 0;
  }
}
</style>
